import { KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useRotateIndexNowKey } from '../hooks/use-rotate-index-now-key';
import { indexNowKeyLocation } from '../schemas/index-now';

/**
 * IndexNow tells Bing, Yandex and others about new and changed URLs at once. The key is
 * read-only in the settings form; this card creates or rotates it.
 */
export function IndexNowCard({ store }: { store: Store }) {
  const { t } = useTranslation('stores');
  const rotate = useRotateIndexNowKey(store.id);
  const key = rotate.data?.key ?? store.settings.seo.indexNowKey;
  const location = rotate.data
    ? rotate.data.keyLocation
    : key
      ? indexNowKeyLocation(store, key)
      : null;

  return (
    <FormSection title={t('indexNow.title')} description={t('indexNow.description')}>
      {key ? (
        <dl className="grid gap-1 text-sm">
          <dt className="text-muted-foreground">{t('indexNow.key')}</dt>
          <dd className="font-mono text-[13px] break-all" data-testid="index-now-key">
            {key}
          </dd>
          <dt className="text-muted-foreground mt-2">{t('indexNow.location')}</dt>
          <dd className="font-mono text-[13px] break-all" data-testid="index-now-location">
            {location ?? t('indexNow.noDomain')}
          </dd>
        </dl>
      ) : (
        <p className="text-muted-foreground text-sm">{t('indexNow.none')}</p>
      )}
      {key ? (
        <Alert>
          <KeyRound aria-hidden />
          <AlertDescription>{t('indexNow.serveHint')}</AlertDescription>
        </Alert>
      ) : null}
      <Can permission="content.write">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={rotate.isPending}
          onClick={() => rotate.mutate()}
        >
          <KeyRound aria-hidden />
          {rotate.isPending
            ? t('indexNow.generating')
            : key
              ? t('indexNow.rotate')
              : t('indexNow.generate')}
        </Button>
        {key ? <p className="text-muted-foreground text-xs">{t('indexNow.rotateHint')}</p> : null}
      </Can>
    </FormSection>
  );
}
